import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import Img from '../../../../../components/Img';
import Location from '../../../../../components/EventProfile/Location';
import { sportTypeIds, teamPaymentTypeIds } from '../../../../../utils/eventConstants';
import { getRegisteredDivisionsWithNeedsPartner, isDivisionCompletelyRegistered, isDoublesFormat } from '../../../../../utils/events';
import DivisionRegCard from './DivisionRegCard';
import ShareEventCard from './ShareEventCard';
import { PowerFunnelSurfaces } from '../../../../../components/Analytics/constants';
import RecurringInfoPopup from './RecurringInfoPopup';

import styles from './regConfirmation.scss';

const RegConfirmationV3 = (props) => {
    const { isWebView, onShowShareUrlPopup } = props;
    const {
        event,
        invites = [],
        regConfirmation,
        upcomingRecurringEvents,
        currentMemberRegistration,
    } = useSelector(({ eventProfile }) => eventProfile);
    const auth = useSelector(({ auth }) => auth);
    const [showRecurringPopup, setShowRecurringPopup] = useState(false);

    const openRecurringEvents = useMemo(() => (
        (upcomingRecurringEvents.data || []).filter(ev => ev.openRegistration)
    ), [upcomingRecurringEvents.data]);

    useEffect(() => {
        if (event.data?.isRecurringEvent && !showRecurringPopup && openRecurringEvents.length) {
            setShowRecurringPopup(true);
        }
    }, [event.data?.isRecurringEvent, openRecurringEvents]);

    const onRecurringConfirmClick = () => {
        setShowRecurringPopup(false);
    };

    const { registeredDivisionIds = [] } = event.data?.currentMember || {};
    const registeredDivisions = (event.data?.eventDivisions.filter(div => !div.isEventEntryFeeDivision) || [])
        .filter(division => registeredDivisionIds.includes(division.id) && isDivisionCompletelyRegistered(division, event.data));

    const invitations = useMemo(() => {
        const currentMemberInvites = invites.filter(invite => parseInt(invite?.memberId) === parseInt(auth?.user?.id));
        const regConfirmationInvites = regConfirmation?.invites || [];

        if (currentMemberInvites?.length > 0 || regConfirmationInvites.length > 0) {
            return [...currentMemberInvites, ...regConfirmationInvites]
                .map(invite => invite.divisionId)
                .filter((divisionId, index, self) => self.indexOf(divisionId) === index)
                .map((divisionId) => {
                    const regConfirmationInvite = regConfirmationInvites.find(inv => inv.divisionId === divisionId);
                    if (regConfirmationInvite) {
                        return regConfirmationInvite;
                    }

                    const currentMemberInvite = currentMemberInvites.find(inv => inv.divisionId === divisionId);
                    if (currentMemberInvite) {
                        return {
                            divisionId: currentMemberInvite.divisionId,
                            name: currentMemberInvite.inviteeName,
                            inviteUrl: null,
                        };
                    }

                    return null;
                });
        }

        return [];
    }, [invites, auth, regConfirmation]);

    const isTennis = event.data?.sportTypeId === sportTypeIds.TENNIS;

    const isRegisteredInAtLeastOneDoublesDivision = registeredDivisions.some(div => isDoublesFormat(div.divisionFormatTypeId));
    const registeredDivisionsWithPaySeparateType = registeredDivisions.filter(div => isDoublesFormat(div.divisionFormatTypeId) && parseInt(div.teamPaymentTypeId) === teamPaymentTypeIds.PAY_SEPARATELY);
    const hasSentAtLeastOneInvite = invitations.length > 0;
    const shouldShowDoublesPartnerInfo = isRegisteredInAtLeastOneDoublesDivision && (registeredDivisionsWithPaySeparateType.length > 0 || hasSentAtLeastOneInvite);

    const registeredDivisionsWithNeedsPartner = getRegisteredDivisionsWithNeedsPartner(event.data?.currentMember?.registeredDivisions);
    const hasAtLeastOneDivisionWithNeedsPartner = shouldShowDoublesPartnerInfo && registeredDivisionsWithNeedsPartner.length > 0;
    const areAllRegisteredDivisionsNeedsPartner = shouldShowDoublesPartnerInfo && registeredDivisions.length > 0 && registeredDivisionsWithNeedsPartner.length === registeredDivisions.length;

    const organizerEmail = event.data?.email;
    const organizerPhone = event.data?.phone;

    if (event.data) {
        return (
            <div>
                <div className={styles.regConfirmationPage}>
                    <div className={styles.content}>
                        <div className={styles.section}>
                            <div className={`d-flex aic ${styles.sectionTitle} ${styles.success}`} data-testid="event-profile.reg-confirmation.registration-details-title">
                                <Img src="/img/v4/icon-valid-green.svg" className="mr8 mt0" />Registration Details
                            </div>
                            <p data-testid="event-profile.reg-confirmation.email-reminder">
                                You will receive an email with a copy of your details. Please check your inbox shortly.
                            </p>
                        </div>
                        <div className={styles.section}>
                            <div className={`${styles.sectionTitle} ${styles.warning}`} data-testid="event-profile.reg-confirmation.waitlist-note-title">
                                Note: If you registered for a waitlist.
                            </div>
                            <p data-testid="event-profile.reg-confirmation.waitlist-note-description">
                                Please be sure to monitor the event and confirm your status before making travel plans. You will receive a full refund for any divisions you do not get into. Contact the event provider with any questions.
                            </p>
                        </div>
                        <div className={styles.section}>
                            {registeredDivisions.map(div => {
                                const currentMemberDivObj = (currentMemberRegistration.data?.registeredDivisions || [])
                                    .find(regDiv => regDiv.id === div.id);
                                return (
                                    <DivisionRegCard
                                        key={`div-reg-card-${div.id}`}
                                        division={div}
                                        invites={invitations}
                                        currentMemberDivObj={currentMemberDivObj}
                                        loading={currentMemberRegistration.loading}
                                        error={currentMemberRegistration.error}
                                    />
                                );
                            })}
                        </div>
                        <div className={styles.section}>
                            <div data-testid="event-profile.reg-confirmation.contact-info">
                                For any questions please contact the event provider at {!!organizerEmail && <><a href={`mailto:${organizerEmail}`} data-testid="event-profile.reg-confirmation.organizer-email-link">{organizerEmail}</a> or </>}{organizerPhone}.
                            </div>
                        </div>
                        {shouldShowDoublesPartnerInfo && (
                            <div className={styles.section}>
                                <div className={`${styles.sectionTitle} ${styles.regular}`} data-testid="event-profile.reg-confirmation.doubles-info-title">
                                    Important Doubles Partner Registration Information
                                </div>
                                <ul className={styles.bulletList}>
                                    {!areAllRegisteredDivisionsNeedsPartner && (
                                        <>
                                            {registeredDivisionsWithPaySeparateType.length > 0 && (
                                                <>
                                                    <li className={styles.listItem}>
                                                        <div className={styles.title} data-testid="event-profile.reg-confirmation.invited-partner-title">
                                                            If you invited your doubles partner during your registration.
                                                        </div>
                                                        <div data-testid="event-profile.reg-confirmation.invited-partner-body">
                                                            They have received an email invitation and your team&#39;s registration won&#39;t be final until they complete  their registration.
                                                        </div>
                                                    </li>
                                                    <li className={styles.listItem}>
                                                        <div className={styles.title} data-testid="event-profile.reg-confirmation.invited-partner-self-title">
                                                            If you&#39;re the &#39;invited&#39; partner who just registered.
                                                        </div>
                                                        <div data-testid="event-profile.reg-confirmation.invited-partner-self-body">
                                                            Please disregard the bullet above. There is no further action you or your partner need to take at this time.
                                                        </div>
                                                    </li>
                                                </>
                                            )}
                                            {hasSentAtLeastOneInvite && (
                                                <li className={styles.listItem}>
                                                    <div className={styles.title} data-testid="event-profile.reg-confirmation.email-invite-title">
                                                        If you have invited your doubles partner via email.
                                                    </div>
                                                    <div data-testid="event-profile.reg-confirmation.email-invite-body">
                                                        They have received an email invitation with a unique registration link. They MUST use this link for registration to ensure proper pairing as your event partner. You can also select the &quot;Share registration link&quot; to copy and share with them their unique link to complete registration.
                                                    </div>
                                                </li>
                                            )}
                                        </>
                                    )}
                                    {hasAtLeastOneDivisionWithNeedsPartner && (
                                        <li className={styles.listItem}>
                                            <div className={styles.title} data-testid="event-profile.reg-confirmation.needs-partner-title">
                                                If you&#39;ve registered for a doubles division without a partner.
                                            </div>
                                            <div data-testid="event-profile.reg-confirmation.needs-partner-body">
                                                Your name will appear on the &#39;Need Partner&#39; section for your division. There you can find and connect with other players seeking a partner. Once you find a partner, you can add them by editing your registration or by contacting the event organizer.
                                            </div>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                        <div className={styles.section}>
                            <div className="d-inline link clickable" onClick={props.backToEvent} data-testid="event-profile.reg-confirmation.view-event-link">View the event</div>
                            {!isWebView && (
                                <>
                                    &nbsp;|&nbsp;<Link className="d-inline mt24" to={`/home`} data-testid="event-profile.reg-confirmation.home-link">Return to home dashboard</Link>
                                </>
                            )}
                        </div>
                        <div className={styles.section}>
                            <ShareEventCard isTennis={isTennis} onShowShareUrlPopup={onShowShareUrlPopup} />
                        </div>
                    </div>
                    <div className={styles.sidebar}>
                        <div className={styles.section}>
                            <div className={`${styles.sectionTitle} ${styles.regular} fw400 text-uppercase`} data-testid="event-profile.reg-confirmation.tournament-location-title">
                                Tournament Location
                            </div>
                            {event.data && (
                                <Location
                                    event={event.data}
                                    surface={PowerFunnelSurfaces.EVENT_GP_FIND_ACCOMMODATIONS_REGISTRATION_CONFIRMATION}
                                />
                            )}
                        </div>
                    </div>
                    <RecurringInfoPopup
                        recurringEvents={openRecurringEvents}
                        showRecurringPopup={showRecurringPopup}
                        onConfirmClick={onRecurringConfirmClick}
                        timeZone={event.data?.eventSchedule?.timeZone}
                    />
                </div>
            </div>
        );
    }

    return null;
};

RegConfirmationV3.propTypes = {
	backToEvent: PropTypes.func.isRequired,
	isWebView: PropTypes.bool,
	onShowShareUrlPopup: PropTypes.func.isRequired,
};

RegConfirmationV3.defaultProps = {
    isWebView: false,
};

export default RegConfirmationV3;
